import React from 'react';
import { UserStatus } from '@shared/types';

interface StatusIndicatorProps {
  status: UserStatus;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const statusConfig: Record<UserStatus, { color: string; label: string; bgClass: string }> = {
  available: {
    color: '#22c55e',
    label: 'Available',
    bgClass: 'bg-green-500',
  },
  away: {
    color: '#f97316',
    label: 'Away',
    bgClass: 'bg-orange-500',
  },
  busy: {
    color: '#ef4444',
    label: 'Busy',
    bgClass: 'bg-red-500',
  },
  offline: {
    color: '#9ca3af',
    label: 'Offline',
    bgClass: 'bg-gray-400',
  },
};

const sizeClasses = {
  sm: 'w-2 h-2',
  md: 'w-3 h-3',
  lg: 'w-4 h-4',
};

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  size = 'md',
  showLabel = false,
}) => {
  const config = statusConfig[status] || statusConfig.offline;

  if (status === 'offline') {
    return (
      <div className="flex items-center gap-1.5">
        <div
          className={`${sizeClasses[size]} rounded-full border-2 border-gray-400 dark:border-gray-500 flex items-center justify-center`}
          title={config.label}
        >
          <svg
            className="w-full h-full text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        {showLabel && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{config.label}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`${sizeClasses[size]} rounded-full ${config.bgClass}`}
        title={config.label}
      />
      {showLabel && (
        <span className="text-xs text-gray-500 dark:text-gray-400">{config.label}</span>
      )}
    </div>
  );
};

export default StatusIndicator;
